import React, {FC} from 'react';

import {Plural, Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {Banner, BannerTypesEnum} from '../../../../libs/shared/ui/src/components/banners/banner';
import {BannerCloseAction} from '../../../../libs/shared/ui/src/components/banners/bannerCloseAction';
import {BannerIcon} from '../../../../libs/shared/ui/src/components/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../../../libs/shared/ui/src/components/banners/bannersContext';
import {BannerText} from '../../../../libs/shared/ui/src/components/banners/bannerText';
import {IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  ContactsManagerSuccessBannerState,
  ContactsManagerSuccessBannerTypesEnum,
} from '../../../store/panels/app/banners/bannersState';

/*
 * Props.
 */

interface ContactsManagerSuccessBannerProps {
  banner: ContactsManagerSuccessBannerState;
  onClose: () => void;
}

/*
 * Component.
 */

export const ContactsManagerSuccessBanner: FC<ContactsManagerSuccessBannerProps> = (props) => {
  const {banner, onClose} = props;
  const theme = useUserTheme();

  return (
    <Banner
      bannerType={BannerTypesEnum.INLINE}
      color={computeBannerColor(banner, theme)}
      priority={BannerPrioritiesEnum.INFORMATION}
    >
      <BannerCloseAction onClick={onClose} />
      <BannerIcon iconName={computeBannerIcon(banner)} />
      <BannerText>{renderBannerContent(banner)}</BannerText>
    </Banner>
  );
};

/*
 * Helpers.
 */

function renderBannerContent(banner: ContactsManagerSuccessBannerState) {
  switch (banner.contactsManagerSuccessType) {
    case ContactsManagerSuccessBannerTypesEnum.CARDS_MERGED:
      return (
        <Plural
          id="contacts-manager-success-banner-cards-merged"
          value={banner.totalCards}
          one="1 contact has been merged"
          other="# contacts have been merged"
        />
      );
    case ContactsManagerSuccessBannerTypesEnum.CONTACT_CREATED:
      return <Trans id="contacts-manager-success-banner-contact-created">New contact created</Trans>;
    case ContactsManagerSuccessBannerTypesEnum.CONTACTS_ADDED_TO_LIST:
      return (
        <Trans id="contacts-manager-success-banner-contacts-added-to-lists">Contacts added to lists</Trans>
      );
    case ContactsManagerSuccessBannerTypesEnum.CONTACTS_REMOVED_FROM_LIST:
      return (
        <Trans id="contacts-manager-success-banner-contacts-removed-from-lists">
          Contacts removed from lists
        </Trans>
      );
    default:
      return null;
  }
}

function computeBannerColor(banner: ContactsManagerSuccessBannerState, theme: UserTheme) {
  switch (banner.contactsManagerSuccessType) {
    case ContactsManagerSuccessBannerTypesEnum.CONTACT_CREATED:
    case ContactsManagerSuccessBannerTypesEnum.CONTACTS_ADDED_TO_LIST:
    case ContactsManagerSuccessBannerTypesEnum.CONTACTS_REMOVED_FROM_LIST:
      return theme.greys.shade70;
    case ContactsManagerSuccessBannerTypesEnum.CARDS_MERGED:
    default:
      return theme.palette.green.shade40;
  }
}

function computeBannerIcon(banner: ContactsManagerSuccessBannerState): IconName {
  switch (banner.contactsManagerSuccessType) {
    case ContactsManagerSuccessBannerTypesEnum.CONTACT_CREATED:
      return 'contactsFilledSmall';
    case ContactsManagerSuccessBannerTypesEnum.CARDS_MERGED:
    default:
      return 'checkmarkCircleSmall';
  }
}
